import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  Briefcase,
  Building2,
  CreditCard,
  Cpu,
  FileCheck2,
  FileText,
  History,
  Info,
  KeyRound,
  MonitorSmartphone,
  Pencil,
  Receipt,
  RotateCcw,
  Sparkles,
  Timer,
  Trash2,
  User,
  Users,
  Wallet,
} from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { SidebarTabs } from '@/components/sidebar-tabs';
import { Dialog } from '@/components/dialog';
import { Select } from '@/components/select';
import { Menu, type MenuItem } from '@/components/menu';
import {
  useClient,
  useDeleteClient,
  useForceDeleteClient,
  useRestoreClient,
  useUpdateClient,
} from '@/lib/clients';
import { useToast } from '@/components/toast';
import { formatStatus } from '@/lib/format';
import { useFormError } from '@/lib/form-error';
import { LEAD_STAGES, useMoveClientToLeads, type LeadStage } from '@/lib/leads';
import { ClientOverview } from './tabs/overview';
import { ClientContacts } from './tabs/contacts';
import { ClientTimeline } from './tabs/timeline';
import { ClientDocuments } from './tabs/documents';
import { ClientApprovables } from './tabs/approvables';
import { ClientInvoices } from './tabs/invoices';
import { ClientExpenses } from './tabs/expenses';
import { ClientProjects } from './tabs/projects';
import { ClientTime } from './tabs/time';
import { ClientAssets } from './tabs/assets';
import { ClientSubscriptions } from './tabs/subscriptions';
import { ClientBilling } from './tabs/billing';
import { ClientVault } from './tabs/vault';
import { ClientPortalUsers } from './tabs/portal-users';

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const clientId = id ? Number(id) : undefined;
  const navigate = useNavigate();
  const { data: client, isLoading, isError } = useClient(clientId);
  const remove = useDeleteClient();
  const restore = useRestoreClient();
  const forceDelete = useForceDeleteClient();
  const update = useUpdateClient(clientId ?? 0);
  const toast = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [movingToLeads, setMovingToLeads] = useState(false);
  const [confirmForce, setConfirmForce] = useState(false);

  if (isError || (!isLoading && !client)) {
    return (
      <div className="space-y-3 py-8 text-center">
        <p className="text-sm font-medium">Client not found</p>
        <p className="text-sm text-[var(--color-foreground-muted)]">
          This client may have been deleted or the link is out of date.
        </p>
        <Button variant="secondary" onClick={() => navigate('/clients')}>
          Back to clients
        </Button>
      </div>
    );
  }

  if (isLoading || !client) {
    return <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>;
  }

  const isDeleted = client.deleted_at != null;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            {client.kind === 'individual' ? (
              <User
                className="size-5 shrink-0 text-[var(--color-foreground-muted)]"
                aria-label="Individual"
              />
            ) : (
              <Building2
                className="size-5 shrink-0 text-[var(--color-foreground-muted)]"
                aria-label="Company"
              />
            )}
            {client.name}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-[var(--color-foreground-muted)]">
            {client.legal_name ? <span>{client.legal_name}</span> : null}
            <Badge tone={client.status === 'active' ? 'success' : 'neutral'}>
              {formatStatus(client.status)}
            </Badge>
            {isDeleted ? <Badge tone="danger">Deleted</Badge> : null}
            {client.tags.map((t) => (
              <Badge key={t} tone="neutral">
                {t}
              </Badge>
            ))}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {isDeleted ? (
            <>
              <Button
                variant="secondary"
                onClick={async () => {
                  await restore.mutateAsync(client.id);
                  toast.success(`${client.name} restored`);
                }}
              >
                <RotateCcw className="size-3.5" /> Restore
              </Button>
              <Button variant="danger" onClick={() => setConfirmForce(true)}>
                <AlertTriangle className="size-3.5" /> Delete forever
              </Button>
            </>
          ) : (
            <>
              <Link to={`/clients/${client.id}/edit`}>
                <Button variant="secondary">
                  <Pencil className="size-3.5" /> Edit
                </Button>
              </Link>
              <Menu
                items={
                  [
                    {
                      key: 'toggle',
                      label: client.status === 'archived' ? 'Reactivate' : 'Archive',
                      icon: client.status === 'archived' ? ArchiveRestore : Archive,
                      onSelect: async () => {
                        const next = client.status === 'archived' ? 'active' : 'archived';
                        await update.mutateAsync({ status: next });
                        toast.success(
                          next === 'archived'
                            ? `${client.name} archived`
                            : `${client.name} reactivated`,
                        );
                      },
                    },
                    {
                      key: 'to-leads',
                      label: 'Move back to leads',
                      icon: Sparkles,
                      onSelect: () => setMovingToLeads(true),
                    },
                    {
                      key: 'delete',
                      label: 'Delete client',
                      icon: Trash2,
                      onSelect: () => setConfirmDelete(true),
                      danger: true,
                    },
                  ] satisfies MenuItem[]
                }
              />
            </>
          )}
        </div>
      </div>

      <SidebarTabs
        paramName="tab"
        tabs={[
          { key: 'overview', label: 'Overview', icon: Info, content: <ClientOverview client={client} /> },
          { key: 'time', label: 'Time', icon: Timer, group: 'Operations', content: <ClientTime clientId={client.id} /> },
          { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard, group: 'Operations', content: <ClientSubscriptions clientId={client.id} /> },
          { key: 'billing', label: 'Billing', icon: CreditCard, group: 'Operations', content: <ClientBilling clientId={client.id} /> },
          { key: 'invoices', label: 'Invoices', icon: Receipt, group: 'Operations', content: <ClientInvoices clientId={client.id} /> },
          { key: 'expenses', label: 'Expenses', icon: Wallet, group: 'Operations', content: <ClientExpenses clientId={client.id} /> },
          { key: 'approvables', label: 'Approvables', icon: FileCheck2, group: 'Operations', content: <ClientApprovables clientId={client.id} /> },
          { key: 'projects', label: 'Projects', icon: Briefcase, group: 'Records', content: <ClientProjects clientId={client.id} /> },
          { key: 'contacts', label: 'Contacts', icon: Users, group: 'Records', content: <ClientContacts clientId={client.id} /> },
          { key: 'documents', label: 'Documents', icon: FileText, group: 'Records', content: <ClientDocuments clientId={client.id} /> },
          { key: 'vault', label: 'Vault', icon: KeyRound, group: 'Records', content: <ClientVault clientId={client.id} /> },
          { key: 'assets', label: 'Assets', icon: Cpu, group: 'Records', content: <ClientAssets clientId={client.id} /> },
          { key: 'timeline', label: 'Timeline', icon: History, group: 'History', content: <ClientTimeline clientId={client.id} /> },
          { key: 'portal', label: 'Portal access', icon: MonitorSmartphone, group: 'Settings', content: <ClientPortalUsers client={client} /> },
        ]}
      />

      {movingToLeads ? (
        <MoveToLeadsDialog
          clientId={client.id}
          clientName={client.name}
          onClose={() => setMovingToLeads(false)}
        />
      ) : null}

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Delete ${client.name}?`}
      >
        <p className="text-sm text-[var(--color-foreground-muted)]">
          The client and its contacts, documents, projects, and timeline will be hidden from the
          app. You can restore them within 30 days from the deleted-clients view.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={async () => {
              await remove.mutateAsync(client.id);
              setConfirmDelete(false);
              navigate('/clients');
            }}
          >
            {remove.isPending ? 'Deleting…' : 'Delete client'}
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={confirmForce}
        onClose={() => setConfirmForce(false)}
        title={`Permanently delete ${client.name}?`}
      >
        <p className="text-sm text-[var(--color-foreground-muted)]">
          This cannot be undone. All contacts, documents, projects, approvables, and timeline
          history will be removed.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmForce(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={forceDelete.isPending}
            onClick={async () => {
              await forceDelete.mutateAsync(client.id);
              setConfirmForce(false);
              navigate('/clients');
            }}
          >
            {forceDelete.isPending ? 'Deleting…' : 'Delete forever'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

/**
 * For a client that never actually closed. The client is archived and kept
 * linked to the lead, so nothing is lost and winning the lead later brings
 * this same client back.
 */
function MoveToLeadsDialog({
  clientId,
  clientName,
  onClose,
}: {
  clientId: number;
  clientName: string;
  onClose: () => void;
}) {
  const move = useMoveClientToLeads();
  const navigate = useNavigate();
  const toast = useToast();
  const { error, wrap } = useFormError();
  const [stage, setStage] = useState<LeadStage>('contacted');

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    let leadId: number | null = null;
    const ok = await wrap(async () => {
      leadId = (await move.mutateAsync({ clientId, stage })).id;
    });
    if (ok && leadId !== null) {
      toast.success(`${clientName} moved back to leads`);
      navigate(`/leads/${leadId}`);
    }
  }

  return (
    <Dialog open onClose={onClose} title="Move back to leads">
      <form className="space-y-4" onSubmit={submit}>
        <p className="text-sm text-[var(--color-foreground-muted)]">
          {clientName} goes back into the pipeline as a lead. The client record is archived, not deleted:
          its contacts, notes and documents stay put, and if you win the lead later, Convert brings this
          client back.
        </p>
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">Pipeline stage</span>
          <Select value={stage} onChange={(e) => setStage(e.target.value as LeadStage)}>
            {OPEN_STAGES.map((s) => (
              <option key={s} value={s}>
                {formatStatus(s)}
              </option>
            ))}
          </Select>
        </label>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={move.isPending}>
            <Sparkles className="size-3.5" /> {move.isPending ? 'Moving…' : 'Move to leads'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Stages a lead can be reopened at: anything short of won or lost. */
const OPEN_STAGES: LeadStage[] = LEAD_STAGES.filter((s) => s !== 'won' && s !== 'lost');
